/**
 * OAuth Callback Handler Component
 *
 * Handles the OAuth callback from Google authentication.
 *
 * Two possible query-string shapes from the backend:
 *
 *  A) Login (existing user): ?token=...&user=...&roles=...&tenant=...&new_user=...&return_url=...
 *     → Store token + user + roles, navigate to return_url.
 *
 *  B) Registration required (brand-new user): ?status=registration_required&pending_token=...&email=...&name=...&tenant=...
 *     → Show the "Complete your signup" modal; after mobile is submitted, the
 *       backend returns the same shape as (A) in JSON and we navigate onward.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import CompleteOAuthRegistrationDialog from './CompleteOAuthRegistrationDialog';

const OAuthCallback = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [processing, setProcessing] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // Registration-required state
    const [needsRegistration, setNeedsRegistration] = useState(false);
    const [pendingToken, setPendingToken] = useState(null);
    const [pendingEmail, setPendingEmail] = useState(null);
    const [pendingName, setPendingName] = useState(null);

    // After registration completes, we reuse this to navigate
    const [completedReturnUrl, setCompletedReturnUrl] = useState(null);

    const storeAuthAndNavigate = ({ token, user, roles, tenant, newUser, returnUrl }) => {
        localStorage.setItem('authToken', token);
        if (user) localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('isAuthenticated', 'true');
        if (roles) localStorage.setItem('userRoles', JSON.stringify(roles));
        if (tenant) localStorage.setItem('tenantData', JSON.stringify({ id: tenant }));
        if (newUser) localStorage.setItem('new_user', 'true');

        setSuccess(true);
        setTimeout(() => navigate(returnUrl || '/dashboard'), 500);
    };

    useEffect(() => {
        const handleCallback = () => {
            try {
                const status = searchParams.get('status');
                const errorParam = searchParams.get('error');

                // Error from OAuth provider
                if (errorParam) {
                    setError(
                        errorParam === 'access_denied'
                            ? 'You cancelled the authentication process.'
                            : `Authentication failed: ${errorParam}`
                    );
                    setProcessing(false);
                    return;
                }

                // Branch B: brand-new user — need to collect mobile number
                if (status === 'registration_required') {
                    const pending = searchParams.get('pending_token');
                    const email = searchParams.get('email');
                    const name = searchParams.get('name');

                    if (!pending) {
                        setError('Missing registration session. Please try signing in with Google again.');
                        setProcessing(false);
                        return;
                    }

                    setPendingToken(pending);
                    setPendingEmail(email || '');
                    setPendingName(name || '');
                    setNeedsRegistration(true);
                    setProcessing(false);
                    return;
                }

                // Branch A: existing user — token + user data flow
                const token = searchParams.get('token');
                const userEncoded = searchParams.get('user');
                const rolesEncoded = searchParams.get('roles');
                const tenant = searchParams.get('tenant');
                const newUser = searchParams.get('new_user');
                const returnUrl = searchParams.get('return_url') || '/dashboard';

                if (!token || !tenant) {
                    setError('Missing authentication details. Please try again.');
                    setProcessing(false);
                    return;
                }

                let userData = null;
                if (userEncoded) {
                    try {
                        userData = JSON.parse(atob(userEncoded));
                    } catch (e) {
                        console.error('Failed to decode user data:', e);
                    }
                }

                let rolesData = null;
                if (rolesEncoded) {
                    try {
                        rolesData = JSON.parse(atob(rolesEncoded));
                    } catch (e) {
                        console.error('Failed to decode roles data:', e);
                    }
                }

                storeAuthAndNavigate({
                    token,
                    user: userData,
                    roles: rolesData,
                    tenant,
                    newUser: newUser === 'true',
                    returnUrl,
                });
            } catch (err) {
                console.error('OAuth callback processing error:', err);
                setError('An unexpected error occurred. Please try again.');
                setProcessing(false);
            }
        };

        handleCallback();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const handleRegistrationSuccess = (data) => {
        setNeedsRegistration(false);
        setCompletedReturnUrl(data.return_url || '/dashboard');
        storeAuthAndNavigate({
            token: data.token,
            user: data.user,
            roles: data.roles,
            tenant: data.tenant?.id,
            newUser: data.new_user,
            returnUrl: data.return_url,
        });
    };

    const handleRegistrationClose = () => {
        setNeedsRegistration(false);
        setError('Registration cancelled. Please try signing in with Google again.');
    };

    // Registration-required state — show modal over a subtle background
    if (needsRegistration) {
        return (
            <>
                <div className="flex align-items-center justify-content-center min-h-screen min-w-screen overflow-hidden">
                    <Card className="text-center p-4 surface-card shadow-2 border-round">
                        <div className="mb-3">
                            <i className="pi pi-user-edit text-primary" style={{ fontSize: '3rem' }}></i>
                        </div>
                        <h3 className="text-900 font-medium mb-2">One more step</h3>
                        <p className="text-600 line-height-3 m-0">
                            Please provide your mobile number to complete the signup.
                        </p>
                    </Card>
                </div>
                <CompleteOAuthRegistrationDialog
                    visible={needsRegistration}
                    email={pendingEmail}
                    name={pendingName}
                    pendingToken={pendingToken}
                    onSuccess={handleRegistrationSuccess}
                    onClose={handleRegistrationClose}
                />
            </>
        );
    }

    if (processing && !error) {
        return (
            <div className="flex align-items-center justify-content-center min-h-screen min-w-screen overflow-hidden">
                <Card className="text-center p-4 surface-card shadow-2 border-round">
                    <div className="mb-4">
                        <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
                    </div>
                    <h3 className="text-900 font-medium mb-2">Completing Sign-In</h3>
                    <p className="text-600 line-height-3 m-0">
                        Please wait while we finish signing you in with Google...
                    </p>
                </Card>
            </div>
        );
    }

    if (success) {
        return (
            <div className="flex align-items-center justify-content-center min-h-screen min-w-screen overflow-hidden">
                <Card className="text-center p-4 surface-card shadow-2 border-round">
                    <div className="mb-4">
                        <i className="pi pi-check-circle text-green-500" style={{ fontSize: '3rem' }}></i>
                    </div>
                    <h3 className="text-900 font-medium mb-2">Welcome!</h3>
                    <p className="text-600 line-height-3 mb-4">
                        Authentication successful. Redirecting you to your dashboard...
                    </p>
                    <ProgressSpinner style={{ width: '30px', height: '30px' }} strokeWidth="4" animationDuration=".8s" />
                </Card>
            </div>
        );
    }

    return (
        <div className="flex align-items-center justify-content-center min-h-screen min-w-screen overflow-hidden">
            <Card className="text-center p-4 surface-card shadow-2 border-round" style={{ maxWidth: '500px' }}>
                <div className="mb-4">
                    <i className="pi pi-exclamation-triangle text-orange-500" style={{ fontSize: '3rem' }}></i>
                </div>
                <h3 className="text-900 font-medium mb-3">Authentication Failed</h3>
                <Message severity="error" text={error} className="mb-4 w-full" />
                <Button label="Go Back to Login" icon="pi pi-arrow-left" onClick={() => navigate('/app/auth/login-v2')} />
            </Card>
        </div>
    );
};

export default OAuthCallback;
